import type { DomainError } from '@core/domain';
import { Spinner } from '@soteria/design-system-ve';

import { ErrorBanner } from '../ErrorBanner';

import * as styles from './Guide.css';
import { GuideCommunityPages } from './GuideCommunityPages';
import { GuideFavoritesSection } from './GuideFavoritesSection';
import { GuideGroupsList } from './GuideGroupsList';
import { GuideSystemPages } from './GuideSystemPages';
import type { CommunityGroupWithCommunities, GuideCommunity } from './useCommunityGroupsWithCommunities';

interface GuideContentProps {
  loading: boolean;
  errors: DomainError[] | null;
  activeTab: number;
  isSystemAdmin: boolean;
  isUserPreferencesEnabled: boolean;
  communityGroups: CommunityGroupWithCommunities[];
  currentView: 'groups' | 'pages';
  selectedCommunity: GuideCommunity | null;
  communityGroupId: string | null;
  communityId?: string;
  selectedGroupId?: string;
  onCommunitySelect: (community: GuideCommunity) => void;
  onBack: () => void;
  onClose: () => void;
}

// Tab identifiers (when USER_PREFERENCES is enabled)
const TAB_FAVORITES = 0;
const TAB_COMMUNITIES = 1;
const TAB_SYSTEM = 2;

// Tab identifiers (when USER_PREFERENCES is disabled)
const TAB_COMMUNITIES_NO_FAVORITES = 0;
const TAB_SYSTEM_NO_FAVORITES = 1;

/**
 * GuideContent component
 * Handles conditional rendering of Guide tab content
 * Simplified to directly render community groups or pages without wrapper components
 */
// eslint-disable-next-line complexity
export function GuideContent({
  loading,
  errors,
  activeTab,
  isSystemAdmin,
  isUserPreferencesEnabled,
  communityGroups,
  currentView,
  selectedCommunity,
  communityGroupId,
  communityId,
  selectedGroupId,
  onCommunitySelect,
  onBack,
  onClose,
}: GuideContentProps) {
  if (loading) {
    return (
      <div className={styles.loadingState}>
        <Spinner />
      </div>
    );
  }

  if (errors) {
    return (
      <div className={styles.errorState}>
        <ErrorBanner errors={errors} />
      </div>
    );
  }

  // Calculate actual tab indices based on feature flag
  const communitiesTab = isUserPreferencesEnabled ? TAB_COMMUNITIES : TAB_COMMUNITIES_NO_FAVORITES;
  const systemTab = isUserPreferencesEnabled ? TAB_SYSTEM : TAB_SYSTEM_NO_FAVORITES;

  // Favorites tab (only when feature enabled)
  if (isUserPreferencesEnabled && activeTab === TAB_FAVORITES) {
    return <GuideFavoritesSection onClose={onClose} />;
  }

  // Communities tab
  if (activeTab === communitiesTab) {
    return currentView === 'groups' ? (
      <GuideGroupsList
        communityGroups={communityGroups}
        isSystemAdmin={isSystemAdmin}
        selectedCommunityId={communityId ?? null}
        selectedGroupId={selectedGroupId}
        onClose={onClose}
        onCommunitySelect={onCommunitySelect}
      />
    ) : selectedCommunity ? (
      <GuideCommunityPages community={selectedCommunity} communityGroupId={communityGroupId} onBack={onBack} onClose={onClose} />
    ) : null;
  }

  // System tab
  if (activeTab === systemTab && isSystemAdmin) {
    return <GuideSystemPages onClose={onClose} />;
  }

  return null;
}
